import { useEffect, useState } from "react";
import axios from "axios";
import { Film, Loader2, ArrowRight, Heart, ShieldCheck, Globe } from "lucide-react";
import { useTranslation } from "@/i18n";
import LanguagePicker from "@/LanguagePicker";
import HoverPreviewThumb from "@/HoverPreviewThumb";
import HeroRotator from "@/HeroRotator";
import SubmitStory from "@/SubmitStory";
import { usePublicConfig } from "@/usePublicConfig";
import { resolveDonationUrl, getStoryStatus } from "@/donateUrl";
import DiscoveryFilterBar from "@/DiscoveryFilterBar";
import StoryLabelBadges from "@/StoryLabelBadges";
import WorldMap from "@/WorldMap";
import SocialFollowRow from "@/SocialFollowRow";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TONE_ACCENT = {
  urgent: "border-l-[3px] border-l-rose-500/80",
  uplifting: "border-l-[3px] border-l-emerald-500/80",
  informational: "border-l-[3px] border-l-sky-500/60",
};

export default function PublicHome() {
  const [t] = useTranslation();
  const [stories, setStories] = useState(null);
  const [popular, setPopular] = useState([]);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState(() => {
    // Deep-linkable filters — /?country=Spain from the world map etc.
    const p = new URLSearchParams(window.location.search);
    const f = {};
    for (const k of ["main_category", "country", "story_length", "tag", "label", "q"]) {
      if (p.get(k)) f[k] = p.get(k);
    }
    if (p.get("year")) f.year = p.get("year");
    return f;
  });
  const [filtering, setFiltering] = useState(false);
  const [mapCountries, setMapCountries] = useState([]);
  const [hasBooks, setHasBooks] = useState(false);
  const publicCfg = usePublicConfig();

  const CATEGORY_LABEL = {
    charity: t("cat_charity"),
    animal_rescue: t("cat_animal_rescue"),
    social_impact: t("cat_social_impact"),
    environment: t("cat_environment"),
  };

  // Fetch popular once (never filtered — always the true top).
  useEffect(() => {
    axios
      .get(`${API}/stories/popular?limit=8`)
      .then(({ data }) => setPopular(data || []))
      .catch(() => setPopular([]));
    axios
      .get(`${API}/stories/facets`)
      .then(({ data }) => setMapCountries(data.countries || []))
      .catch(() => setMapCountries([]));
    axios
      .get(`${API}/books`)
      .then(({ data }) => setHasBooks((data.books || []).length > 0))
      .catch(() => setHasBooks(false));
  }, []);

  // Fetch the curated hero showcase (variety across main_categories,
  // date-seeded so stable per day). Independent of the filter state so
  // the hero stays as a "what Storylo covers" reel even when a visitor
  // narrows the grid below to a single category.
  const [heroStories, setHeroStories] = useState([]);
  useEffect(() => {
    axios
      .get(`${API}/stories/hero?limit=8`)
      .then(({ data }) => setHeroStories(data || []))
      .catch(() => setHeroStories([]));
  }, []);

  // Refetch stories whenever filters change. Debounced for the free-text
  // search input (which typically changes every keystroke).
  useEffect(() => {
    const params = {};
    for (const k of ["main_category", "country", "story_length", "tag", "label", "q"]) {
      if (filters[k]) params[k] = filters[k];
    }
    if (filters.year != null && filters.year !== "") params.year = filters.year;
    setFiltering(true);
    const cancel = axios.CancelToken.source();
    axios
      .get(`${API}/stories`, { params, cancelToken: cancel.token })
      .then(({ data }) => {
        setStories(data);
        setError("");
      })
      .catch((e) => {
        if (axios.isCancel(e)) return;
        setError("Couldn't load stories right now. Please try again shortly.");
      })
      .finally(() => setFiltering(false));
    return () => cancel.cancel();
  }, [filters]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 text-center">
        <p className="font-display text-xl">{error}</p>
      </div>
    );
  }
  if (stories === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[color:var(--accent)]" />
      </div>
    );
  }

  // Hero showcase: prefer the curated /api/stories/hero list (variety
  // across main_categories, date-seeded so it's stable-per-day). Falls
  // back to the old view-ranked merge if that endpoint returned empty
  // (e.g. brand-new deploy with no stories yet).
  const heroPicks = heroStories.length > 0
    ? heroStories.slice(0, 8)
    : (() => {
        const seen = new Set();
        const merged = [...(popular || []), ...(stories || [])].filter((s) => {
          if (!s || !s.slug || seen.has(s.slug)) return false;
          seen.add(s.slug);
          return true;
        });
        return merged.slice(0, 3);
      })();

  return (
    <div className="min-h-screen" data-testid="public-home">
      <header className="px-6 md:px-10 py-6 md:py-8 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand-s.png" alt="Storylo" className="w-9 h-9 rounded-full" data-testid="brand-logo" />
            <div>
              <div className="font-display text-lg leading-none">Storylo</div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--muted)] mt-1">
                {t("tagline")}
              </div>
            </div>
          </div>
          <nav className="flex flex-wrap items-center justify-end gap-3 sm:gap-4 text-sm">
            <a href="/" className="text-white">{t("nav_all")}</a>
            <a href="/map" className="text-[color:var(--muted)] hover:text-white transition-colors" data-testid="nav-map">{t("nav_map")}</a>
            {hasBooks && (
              <a href="/books" className="text-[color:var(--muted)] hover:text-white transition-colors" data-testid="nav-books">{t("nav_books")}</a>
            )}
            <a href="/impact" className="text-[color:var(--muted)] hover:text-white transition-colors" data-testid="nav-impact">{t("nav_impact")}</a>
            <a href="/kids" className="text-[color:var(--muted)] hover:text-white transition-colors" data-testid="nav-kids">Kids</a>
            <a href="/about" className="text-[color:var(--muted)] hover:text-white transition-colors" data-testid="nav-about">{t("nav_about")}</a>
            <LanguagePicker />
          </nav>
        </div>
      </header>

      {/* Announcement — Storylo Kids launch */}
      <div className="px-6 md:px-10 pt-6" data-testid="home-kids-announcement">
        <div
          className="max-w-[1200px] mx-auto rounded-3xl px-6 py-6 md:px-10 md:py-8"
          style={{ background: "#FFF8EC", border: "3px solid #FFE3A3", color: "#4A3B2A" }}
        >
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <img
              src="/tillo-friends.png"
              alt="Tillo the Storysaur and his friends"
              className="w-full max-w-[320px] md:max-w-[400px] rounded-2xl drop-shadow-xl shrink-0"
              data-testid="home-announcement-img"
            />
            <div className="text-center md:text-left">
              <div
                className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-3"
                style={{ background: "#3E6E64", color: "#FFFFFF" }}
              >
                ✨ New to Storylo
              </div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight" style={{ color: "#3E6E64" }}>
                Something New for Little Ones Around the World! 🌍✨
              </h2>
              <p className="leading-relaxed text-[15px] sm:text-base mt-3 max-w-2xl">
                Meet <strong>Tillo the Storysaur</strong>, a friendly little dinosaur with a world of
                stories to share! Join Tillo and his lovable friends for colourful adventures, cheerful
                songs and gentle lessons about kindness, friendship and never giving up.
              </p>
              <p className="leading-relaxed text-[15px] sm:text-base mt-3 max-w-2xl" style={{ color: "#8A7A5E" }}>
                Inspired by the timeless Aesop's fables, these lovely little stories are made for
                little ones — and the grown-ups who watch with them. And coming soon: private,{" "}
                <a href="/kids/about" className="font-semibold underline underline-offset-2" style={{ color: "#C98A2B" }} data-testid="home-announcement-personalised-link">
                  personalised video adventures
                </a>{" "}
                where your child can join Tillo in a story of their own.
              </p>
              <a
                href="/kids"
                className="inline-block mt-5 px-7 py-3 rounded-full font-bold text-sm text-white transition-transform hover:scale-[1.03]"
                style={{ background: "#3E6E64" }}
                data-testid="home-announcement-kids-link"
              >
                Click the Kids tab and let the adventures begin!
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Hero rotator — auto-rotating cinematic preview of the top 3
          most-watched stories (or most-recent if no story has views yet).
          Self-hides when there's nothing published. */}
      <HeroRotator stories={heroPicks} t={t} />

      <main className="px-6 md:px-10 py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">            {t("home_h1_a")}
            <em className="block text-[color:var(--accent)] not-italic mt-2">{t("home_h1_b")}</em>
          </h1>
          <p className="text-[color:var(--muted)] max-w-2xl mt-5 leading-relaxed text-base sm:text-lg">
            {t("home_sub")}
          </p>

          <section className="mt-14 md:mt-20 panel p-7 md:p-10 max-w-3xl border-l-[3px] border-l-[color:var(--accent)]" data-testid="home-about">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-4">
              {t("about_eyebrow")}
            </div>
            <p className="leading-relaxed text-[15px] sm:text-base">
              {t("about_p1")}
            </p>
            <p className="leading-relaxed text-[15px] sm:text-base mt-4 text-[color:var(--muted)]">
              {t("about_p2")}
            </p>

            <div className="mt-8 pt-7 border-t border-white/10" data-testid="editorial-principles">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-4">
                {t("ep_title")}
              </div>
              <div className="font-display text-xl sm:text-2xl leading-snug">
                {t("ep_tag1")}
                <span className="block text-[color:var(--accent)]">{t("ep_tag2")}</span>
              </div>
              <p className="leading-relaxed text-[15px] sm:text-base mt-4">
                {t("ep_p1")}
              </p>
              <p className="leading-relaxed text-[15px] sm:text-base mt-4 text-[color:var(--muted)]">
                {t("ep_p2")}
              </p>
              <p className="leading-relaxed text-[15px] sm:text-base mt-4 text-[color:var(--muted)]">
                {t("ep_p3")}
              </p>
              <p className="leading-relaxed text-[15px] sm:text-base mt-4 text-[color:var(--muted)]">
                {t("ep_p4")}
              </p>
              <p className="leading-relaxed text-[15px] sm:text-base mt-5 italic">
                {t("ep_close")}
              </p>
            </div>
          </section>

          {mapCountries.length > 0 && (
            <section className="mt-12 md:mt-16" data-testid="home-map-teaser">
              <div className="flex items-baseline justify-between mb-5">
                <div>
                  <div className="text-[10px] tracking-[0.22em] uppercase text-[color:var(--accent)] mb-2">
                    {t("home_map_eyebrow")}
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                    {t("home_map_title")}
                  </h2>
                </div>
                <a href="/map" className="text-xs uppercase tracking-[0.18em] text-[color:var(--muted)] hover:text-[color:var(--accent)] transition-colors inline-flex items-center gap-1.5" data-testid="home-map-open">
                  {t("home_map_open")} <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
              <a href="/map" className="block panel p-3 md:p-5 hover:border-[color:var(--accent)]/40 transition-colors">
                <div className="pointer-events-none">
                  <WorldMap countries={mapCountries} interactive={false} />
                </div>
                <div className="text-xs text-[color:var(--muted)] mt-3 text-center">
                  {t("home_map_sub")}
                </div>
              </a>
            </section>
          )}

          {popular.length >= 3 && (
            <section className="mt-12 md:mt-16" data-testid="popular-rail">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-2xl md:text-3xl tracking-tight">
                  Most watched
                </h2>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
                  this week · viewer-ranked
                </span>
              </div>
              <div className="-mx-4 px-4 md:mx-0 md:px-0 flex gap-4 overflow-x-auto md:overflow-visible md:grid md:grid-cols-2 lg:grid-cols-4 snap-x snap-mandatory scrollbar-hide pb-2">
                {popular.map((s, idx) => (
                  <a
                    key={`pop-${s.slug}`}
                    href={`/s/${s.slug}`}
                    className="panel overflow-hidden group block min-w-[78%] sm:min-w-[48%] md:min-w-0 snap-start"
                    data-testid={`popular-card-${s.slug}`}
                  >
                    <div className="relative">
                      <HoverPreviewThumb story={s} />
                      <span className="absolute top-3 left-3 text-[11px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-[color:var(--accent)] text-black font-semibold z-10">
                        #{idx + 1}
                      </span>
                      {s.view_count > 0 && (
                        <span className="absolute bottom-3 right-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white inline-flex items-center gap-1 z-10">
                          {s.view_count.toLocaleString()} {s.view_count === 1 ? "view" : "views"}
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <div className="font-display text-base leading-snug group-hover:text-[color:var(--accent)] transition-colors line-clamp-2">
                        {s.title}
                      </div>
                      {s.location && (
                        <div className="text-[11px] text-[color:var(--muted)] mt-1.5 truncate">
                          {s.location}
                        </div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {stories.length === 0 ? (
            <div className="mt-16">
              <DiscoveryFilterBar value={filters} onChange={setFilters} />
              <div className="panel p-10 text-center">
                <Film className="w-10 h-10 mx-auto text-[color:var(--muted)] opacity-50" />
                <div className="font-display text-lg mt-4">
                  {Object.keys(filters).length ? "No stories match those filters" : t("home_empty_title")}
                </div>
                <div className="text-sm text-[color:var(--muted)] mt-2">
                  {Object.keys(filters).length ? (
                    <button
                      onClick={() => setFilters({})}
                      className="underline underline-offset-2 hover:text-white"
                      data-testid="empty-clear-filters"
                    >
                      Clear filters and browse everything
                    </button>
                  ) : (
                    t("home_empty_sub")
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-12 md:mt-16">
              <DiscoveryFilterBar value={filters} onChange={setFilters} />
              {filtering && stories.length === 0 && (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-5 h-5 animate-spin text-[color:var(--accent)]" />
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="stories-grid">
                {stories.map((s) => {
                const tone = TONE_ACCENT[s.emotional_tone] || "";
                const donate = resolveDonationUrl(s, publicCfg);
                const status = getStoryStatus(s);
                return (
                <div
                  key={s.slug}
                  className={`panel overflow-hidden group block ${tone}`}
                  data-testid={`story-card-${s.slug}`}
                >
                  <a href={`/s/${s.slug}`} className="block">
                    <div className="relative">
                      <HoverPreviewThumb story={s} />
                      {s.category && s.category !== "general" && (
                        <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white z-10">
                          {CATEGORY_LABEL[s.category] || s.category}
                        </span>
                      )}
                      {s.verified && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-emerald-500/85 text-black font-medium z-10" title={t("badge_verified")}>
                          <ShieldCheck className="w-3 h-3" /> {t("badge_verified")}
                        </span>
                      )}
                      {(s.languages_ready || 0) >= 2 && (
                        <span
                          className="absolute bottom-3 right-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded-full bg-black/70 backdrop-blur text-white z-10"
                          data-testid={`story-languages-badge-${s.slug}`}
                        >
                          <Globe className="w-3 h-3" /> {t("card_languages", { n: s.languages_ready })}
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="font-display text-lg leading-snug group-hover:text-[color:var(--accent)] transition-colors">
                        {s.title}
                      </div>
                      {(s.labels && s.labels.length > 0) && (
                        <div className="mt-2">
                          <StoryLabelBadges labels={s.labels} size="xs" />
                        </div>
                      )}
                      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)] mt-2 flex items-center justify-between">
                        <span>
                          {new Date(s.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                          {s.country && <span className="ml-2 opacity-70">· {s.country}</span>}
                          {s.year != null && <span className="ml-2 opacity-70">· {s.year}</span>}
                        </span>
                        <ArrowRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </a>
                  {donate && (
                    <div className="px-5 pb-5">
                      <a href={donate.url} target="_blank" rel="noopener noreferrer"
                         className="btn-primary text-xs inline-flex items-center gap-1.5 w-full justify-center"
                         style={{ background: "#10b981", color: "#04150d" }}
                         data-testid={`story-support-${s.slug}`}>
                        <Heart className="w-3 h-3" /> {t("card_support_cause")}
                      </a>
                    </div>
                  )}
                  {status === "goal_reached" && (
                    <div className="px-5 pb-5">
                      <span className="inline-flex items-center gap-1.5 w-full justify-center text-xs px-3 py-2 rounded-md border"
                            style={{ background: "rgba(16, 185, 129, 0.1)", borderColor: "rgba(16, 185, 129, 0.3)", color: "#34d399" }}
                            data-testid={`story-goal-reached-${s.slug}`}>
                        <Heart className="w-3 h-3 fill-current" /> Goal reached · thank you
                      </span>
                    </div>
                  )}
                </div>
                );
              })}
              </div>
            </div>
          )}
        </div>
      </main>

      <SubmitStory />

      {/* Follow bar — converts homepage browsers into social followers. */}
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 mt-12">
        <SocialFollowRow />
      </div>

      <footer className="mt-16 border-t border-white/5 px-6 md:px-10 py-6">
        <div className="max-w-[1200px] mx-auto flex flex-wrap items-center justify-between gap-3 text-xs text-[color:var(--muted)]">
          <div>
            {t("footer_made_by")} <a href="https://hadfun.co.uk" className="text-white hover:text-[color:var(--accent)] transition-colors">Hadfun</a>
          </div>
          <a href="/studio" className="opacity-30 hover:opacity-100 transition-opacity" data-testid="studio-link">{t("nav_studio")}</a>
        </div>
      </footer>
    </div>
  );
}
